// NetworkStatusContext.js
import React, { createContext, useState, useEffect } from "react";
import axios from "axios";
import { baseUrl } from "../api";   // e.g. "http://localhost:8010"

/** string union: "online" | "offline" */
export const NetworkStatusContext = createContext("online");

/**
 *  NetworkStatusProvider
 *  ---------------------
 *  Wrap once near the root of your app.
 *
 *  Props:
 *    interval   – heartbeat period in ms (default 10000 = 10 s)
 *    children   – React children
 */
export function NetworkStatusProvider({ interval = 10000, children }) {
  const [status, setStatus] = useState("online");

  // 1) Heartbeat ping
  useEffect(() => {
    let timer;

    const ping = async () => {
      try {
        await axios.get(`${baseUrl}/health`, { timeout: 3000 });
        setStatus("online");
      } catch (err) {
        setStatus("offline");
      } finally {
        timer = setTimeout(ping, interval);
      }
    };

    ping();                       // kick-off immediately
    return () => clearTimeout(timer);
  }, [interval]);

  // 2) Global Axios response errors
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      (err) => {
        // Network error → no response; ECONNABORTED covers timeout
        if (!err.response || err.code === "ECONNABORTED") {
          setStatus("offline");
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  return (
    <NetworkStatusContext.Provider value={status}>
      {children}
    </NetworkStatusContext.Provider>
  );
}
